/**
 * Email Service - Energia Info CRM
 * Frontend service for calling the Cloud Run email-service
 * 
 * TEST MODE: When enabled, all emails are sent to the test address instead
 * of the actual recipient. Can be toggled in Organization Settings or via UI.
 * 
 * Priority:
 * 1. localStorage (fast, per-browser)
 * 2. Org billingSettings (source of truth, synced on load)
 */

const EMAIL_SERVICE_URL = process.env.REACT_APP_EMAIL_SERVICE_URL || 'https://email-service-dy7yoaghoq-ew.a.run.app';
const DEFAULT_TEST_EMAIL = process.env.REACT_APP_DEFAULT_TEST_EMAIL || 'nroland89@gmail.com,energiainfokft@gmail.com';
const TEST_MODE_KEY = 'email_test_mode_enabled';
const TEST_ADDRESS_KEY = 'email_test_address';

/**
 * Get the configured test email address
 */
export const getTestEmailAddress = (): string => {
  try {
    return localStorage.getItem(TEST_ADDRESS_KEY) || DEFAULT_TEST_EMAIL;
  } catch {
    return DEFAULT_TEST_EMAIL;
  }
};

export const getTestEmailAddresses = (): string[] => {
  return getTestEmailAddress().split(',').map(e => e.trim()).filter(Boolean);
};

/**
 * Check if email test mode is enabled (from localStorage)
 */
export const isEmailTestMode = (): boolean => {
  try {
    return localStorage.getItem(TEST_MODE_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Enable/disable email test mode locally
 */
export const setEmailTestMode = (enabled: boolean, testAddress?: string): void => {
  try {
    localStorage.setItem(TEST_MODE_KEY, enabled ? 'true' : 'false');
    if (testAddress) {
      localStorage.setItem(TEST_ADDRESS_KEY, testAddress);
    }
  } catch {
    // ignore
  }
};

/**
 * Sync email test mode from organization billing settings
 * Call this when billing settings are loaded
 */
export const syncEmailTestModeFromOrg = (emailTestMode?: boolean, emailTestAddress?: string): void => {
  try {
    if (emailTestMode !== undefined) {
      localStorage.setItem(TEST_MODE_KEY, emailTestMode ? 'true' : 'false');
    }
    if (emailTestAddress) {
      localStorage.setItem(TEST_ADDRESS_KEY, emailTestAddress);
    }
  } catch {
    // ignore
  }
};

/**
 * Get the effective recipient email address
 * In test mode, keeps original recipient and sends BCC to test addresses
 */
const getEffectiveEmail = (originalEmail: string): string => {
  return originalEmail;
};

/**
 * Get the effective recipient list
 * In test mode, keeps original recipients and sends BCC to test addresses
 */
const getEffectiveEmails = (originalEmails: string[]): string[] => {
  return originalEmails;
};

interface SendInvoiceRequest {
  to: string;
  buyer_name: string;
  invoice_number: string;
  issue_date?: string;
  due_date?: string;
  gross_amount: number | string;
  currency: string;
  status?: 'PAID' | 'UNPAID' | 'OVERDUE';
  payment_method?: string;
  notes?: string;
  pdf_base64?: string;
}

interface SendPaymentReminderRequest {
  to: string;
  buyer_name: string;
  invoice_number: string;
  issue_date?: string;
  due_date: string;
  gross_amount: number | string;
  currency: string;
  days_overdue?: number;
  payment_method?: string;
}

interface SendGeneralRequest {
  to: string;
  subject?: string;
  title?: string;
  message: string;
  recipient_name?: string;
  action_url?: string;
  action_text?: string;
  attachments?: Array<{
    filename: string;
    content: string; // base64
    content_type: string;
  }>;
}

interface SendBulkRequest {
  recipients: string[];
  subject?: string;
  title?: string;
  message: string;
  recipient_name?: string;
  action_url?: string;
  action_text?: string;
  attachments?: Array<{
    filename: string;
    content: string;
    content_type: string;
  }>;
}

/**
 * Send invoice email with optional PDF attachment
 */
export const sendInvoiceEmail = async (data: SendInvoiceRequest): Promise<{ success: boolean; message: string; testMode?: boolean; originalEmail?: string; testEmails?: string[] }> => {
  const testMode = isEmailTestMode();
  
  try {
    const response = await fetch(`${EMAIL_SERVICE_URL}/send-invoice`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...data, to: data.to, test_bcc: testMode ? getTestEmailAddresses().join(',') : undefined }),
    });

    const result = await response.json();

    if (response.ok) {
      return { 
        success: true, 
        message: testMode 
          ? `📧 TESZT MÓD: Számla email elküldve az eredeti címre (${data.to}), BCC: ${getTestEmailAddresses().join(', ')}` 
          : result.message,
        testMode,
        originalEmail: data.to,
        testEmails: testMode ? getTestEmailAddresses() : undefined
      };
    } else {
      console.error('Failed to send invoice email:', result.error);
      return { success: false, message: result.error || 'Failed to send email' };
    }
  } catch (error) {
    console.error('Error sending invoice email:', error);
    return { success: false, message: 'Network error while sending email' };
  }
};

/**
 * Send payment reminder email
 */
export const sendPaymentReminder = async (data: SendPaymentReminderRequest): Promise<{ success: boolean; message: string; days_overdue?: number; testMode?: boolean; originalEmail?: string; testEmails?: string[] }> => {
  const testMode = isEmailTestMode();
  
  try {
    const response = await fetch(`${EMAIL_SERVICE_URL}/send-payment-reminder`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...data, to: data.to, test_bcc: testMode ? getTestEmailAddresses().join(',') : undefined }),
    });

    const result = await response.json();

    if (response.ok) {
      return { 
        success: true, 
        message: testMode 
          ? `📧 TESZT MÓD: Emlékeztető elküldve az eredeti címre (${data.to}), BCC: ${getTestEmailAddresses().join(', ')}` 
          : result.message,
        days_overdue: result.days_overdue,
        testMode,
        originalEmail: data.to,
        testEmails: testMode ? getTestEmailAddresses() : undefined
      };
    } else {
      console.error('Failed to send payment reminder:', result.error);
      return { success: false, message: result.error || 'Failed to send reminder' };
    }
  } catch (error) {
    console.error('Error sending payment reminder:', error);
    return { success: false, message: 'Network error while sending reminder' };
  }
};

/**
 * Send general email
 */
export const sendGeneralEmail = async (data: SendGeneralRequest): Promise<{ success: boolean; message: string; testMode?: boolean; originalEmail?: string; testEmails?: string[] }> => {
  const testMode = isEmailTestMode();
  
  try {
    const response = await fetch(`${EMAIL_SERVICE_URL}/send-general`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...data, to: data.to, test_bcc: testMode ? getTestEmailAddresses().join(',') : undefined }),
    });

    const result = await response.json();

    if (response.ok) {
      return { 
        success: true, 
        message: testMode 
          ? `📧 TESZT MÓD: Email elküldve az eredeti címre (${data.to}), BCC: ${getTestEmailAddresses().join(', ')}` 
          : result.message,
        testMode,
        originalEmail: data.to,
        testEmails: testMode ? getTestEmailAddresses() : undefined
      };
    } else {
      console.error('Failed to send general email:', result.error);
      return { success: false, message: result.error || 'Failed to send email' };
    }
  } catch (error) {
    console.error('Error sending general email:', error);
    return { success: false, message: 'Network error while sending email' };
  }
};

/**
 * Send bulk email to multiple recipients
 */
export const sendBulkEmail = async (data: SendBulkRequest): Promise<{ success: boolean; message: string; results?: { success: string[]; failed: string[] }; testMode?: boolean; originalEmails?: string[]; testEmails?: string[] }> => {
  const testMode = isEmailTestMode();
  
  try {
    const response = await fetch(`${EMAIL_SERVICE_URL}/send-bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...data, recipients: data.recipients, test_bcc: testMode ? getTestEmailAddresses().join(',') : undefined }),
    });

    const result = await response.json();

    if (response.ok) {
      return { 
        success: true, 
        message: testMode 
          ? `📧 TESZT MÓD: ${data.recipients.length} email elküldve az eredeti címzetteknek (${data.recipients.join(', ')}), BCC: ${getTestEmailAddresses().join(', ')}` 
          : result.message,
        results: result.results,
        testMode,
        originalEmails: data.recipients,
        testEmails: testMode ? getTestEmailAddresses() : undefined
      };
    } else {
      console.error('Failed to send bulk email:', result.error);
      return { success: false, message: result.error || 'Failed to send emails' };
    }
  } catch (error) {
    console.error('Error sending bulk email:', error);
    return { success: false, message: 'Network error while sending emails' };
  }
};

interface SendInvoiceLinkRequest {
  to: string;
  customer_name: string;
  invoice_number: string;
  billing_period?: string;
  download_url: string;
}

/**
 * Send invoice download link email
 */
export const sendInvoiceLinkEmail = async (data: SendInvoiceLinkRequest): Promise<{ success: boolean; message: string; testMode?: boolean; originalEmail?: string; testEmails?: string[] }> => {
  const testMode = isEmailTestMode();
  
  try {
    const response = await fetch(`${EMAIL_SERVICE_URL}/send-invoice-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        ...data, 
        to: data.to,
        billing_period: data.billing_period || '-',
        test_bcc: testMode ? getTestEmailAddresses().join(',') : undefined
      }),
    });

    const result = await response.json();

    if (response.ok) {
      return { 
        success: true, 
        message: testMode 
          ? `📧 TESZT MÓD: Link elküldve az eredeti címre (${data.to}), BCC: ${getTestEmailAddresses().join(', ')}` 
          : result.message,
        testMode,
        originalEmail: data.to,
        testEmails: testMode ? getTestEmailAddresses() : undefined
      };
    } else {
      console.error('Failed to send invoice link:', result.error);
      return { success: false, message: result.error || 'Failed to send email' };
    }
  } catch (error) {
    console.error('Error sending invoice link:', error);
    return { success: false, message: 'Network error while sending email' };
  }
};

/**
 * Check email service health
 */
export const checkEmailServiceHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${EMAIL_SERVICE_URL}/health`, {
      method: 'GET',
    });
    return response.ok;
  } catch {
    return false;
  }
};
